import { useCallback, useState } from 'react'

import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { toast } from 'sonner'

import { useNavigation } from '~/hooks/useNavigation'
import { useServerRequests } from '~/hooks/useRequests'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { PasskeyConfirmationAction } from '~/types'
import { captureException } from '~/utils/errors'
import {
  getPasskeyErrorDescription,
  startPasskeyAuthentication,
} from '~/utils/passkeys'

export type PasskeyConfirmationRequest = {
  action: PasskeyConfirmationAction
  orgId?: string
  resourceId?: string
}

export const passkeyConfirmationHeaders = (confirmationId: string) => ({
  'X-Passkey-Confirmation': confirmationId,
})

/**
 * Step-up check for sensitive actions. Runs the WebAuthn ceremony against the
 * server's confirm endpoints and resolves to the confirmation id the server
 * issued, or null when the user has no passkey, cancels, or fails. Callers
 * send the id on the gated request as the `X-Passkey-Confirmation` header.
 */
export const usePasskeyConfirmation = () => {
  const { serverPostRequest } = useServerRequests()
  // The confirm endpoint accepts any user passkey, including non-org signers.
  const { passkeys } = useUserPasskeys()
  const hasPasskey = passkeys.length > 0
  const navigate = useNavigation()
  const [isConfirming, setIsConfirming] = useState(false)

  const confirmWithPasskey = useCallback(
    async (request: PasskeyConfirmationRequest): Promise<string | null> => {
      if (!hasPasskey) {
        toast.error('No passkey found', {
          description: 'Add a passkey to continue',
          action: {
            label: 'Settings',
            onClick: () => navigate('/settings/personal/signing-keys'),
          },
        })
        return null
      }

      setIsConfirming(true)
      try {
        const options = await serverPostRequest<{
          confirmationId: string
          options: PublicKeyCredentialRequestOptionsJSON
        }>({
          path: '/api/passkey/confirm/v2/options',
          params: request,
          errorMessage: 'Failed to start passkey verification',
        })
        if (!options) return null

        const attResp = await startPasskeyAuthentication(options.data.options, {
          onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
            if (isUnsupportedError) {
              toast.error('Browser does not support passkeys', {
                description:
                  error instanceof Error ? error.message : String(error),
              })
            } else if (isNotAllowedError) {
              toast.error('Passkey verification cancelled')
            } else {
              toast.error('Failed to verify passkey', {
                description: getPasskeyErrorDescription(error),
              })
              captureException(error)
            }
          },
        })
        if (!attResp) return null

        const verifyResponse = await serverPostRequest<{
          confirmationId: string
        }>({
          path: '/api/passkey/confirm/v2/verify',
          params: {
            confirmationId: options.data.confirmationId,
            credentialId: attResp.id,
            authentication: JSON.stringify(attResp),
          },
          errorMessage: 'Failed to verify passkey',
        })
        return verifyResponse?.data?.confirmationId ?? null
      } catch (error) {
        captureException(error)
        return null
      } finally {
        setIsConfirming(false)
      }
    },
    [hasPasskey, navigate, serverPostRequest],
  )

  return { confirmWithPasskey, isConfirming }
}
